"use client"

import * as React from "react"
import { X } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AppIcon } from "@/components/desk/AppIcon"
import { InstalledApp } from "@/types/workspace"

interface AddAppModalProps {
  isOpen: boolean
  onClose: () => void
  installedApps: InstalledApp[]
  hiddenAppIds: string[]
  setHiddenAppIds: React.Dispatch<React.SetStateAction<string[]>>
}

export function AddAppModal({
  isOpen,
  onClose,
  installedApps,
  hiddenAppIds,
  setHiddenAppIds,
}: AddAppModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-55 flex items-center justify-center bg-black/60 backdrop-blur-xs font-sans">
      <Card className="w-full max-w-md border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-2xl rounded-2xl space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <span>➕</span> Adicionar / Ocultar Apps
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <p className="text-[11px] text-zinc-500">
          Marque ou desmarque os aplicativos abaixo para exibi-los ou ocultá-los da área de trabalho e da tela móvel.
        </p>

        <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
          {installedApps
            .filter((ia) => ia.status === "active")
            .map(({ app }) => {
              const isVisible = !hiddenAppIds.includes(app.id)
              return (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-2.5 rounded-xl border border-zinc-100 dark:border-zinc-850 hover:bg-zinc-50 dark:hover:bg-zinc-955 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="small" />
                    <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200">{app.name}</span>
                  </div>
                  <button
                    onClick={() => {
                      if (isVisible) {
                        setHiddenAppIds((prev) => [...prev, app.id])
                      } else {
                        setHiddenAppIds((prev) => prev.filter((id) => id !== app.id))
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold cursor-pointer transition-all ${
                      isVisible
                        ? "bg-[#004EB1]/10 text-[#004EB1] hover:bg-red-50 hover:text-red-500"
                        : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 hover:bg-[#004EB1]/10 hover:text-[#004EB1]"
                    }`}
                  >
                    {isVisible ? "Visível" : "Oculto"}
                  </button>
                </div>
              )
            })}
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-100 dark:border-zinc-800 pt-4">
          <Button
            onClick={onClose}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold py-1.5 px-4 cursor-pointer"
          >
            Concluir
          </Button>
        </div>
      </Card>
    </div>
  )
}
